import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, ExternalLink, Linkedin, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { SiteFooter } from './SiteFooter';
import { CoverImage } from './CoverImage';
import { sanitizeHtml } from '@/lib/sanitize';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

// Render body: if it looks like HTML, dangerouslySetInnerHTML (trusted admin);
// otherwise split on blank lines into paragraphs.
const renderBody = (body) => {
  if (!body) return null;
  const trimmed = body.trim();
  const looksLikeHtml = /<\/?(p|h[1-6]|ul|ol|li|blockquote|em|strong|a|br|img|div|span)\b/i.test(trimmed);
  if (looksLikeHtml) {
    return (
      <div
        className="blog-body font-body text-[#d4c4a8]/85 leading-relaxed space-y-4"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(trimmed) }}
      />
    );
  }
  const paragraphs = trimmed.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-5 font-body text-[#d4c4a8]/85 leading-relaxed">
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-line">{p}</p>
      ))}
    </div>
  );
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [related, setRelated] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/blog/posts/${slug}/related?limit=3`)
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => setRelated(d.posts || []))
      .catch(() => { /* related section simply hides */ });
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPost = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/api/blog/posts/${slug}`);
        if (res.status === 404) {
          setError('Post not found.');
          return;
        }
        if (!res.ok) throw new Error('Failed to load post');
        const data = await res.json();
        setPost(data.post);
        // Set document title for the tab
        if (data.post?.title) {
          document.title = `${data.post.title} — Marketing Tarot Blog`;
        }
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
    return () => { document.title = 'Marketing Tarot 🔮'; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8]">
      {/* Top bar */}
      <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="post-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </button>
        <a
          href="https://calendly.com/marketingtarot/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-10 px-5 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display tracking-widest shadow-lg shadow-[#e89b47]/25 rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
          data-testid="post-book-call-btn"
        >
          Book a Clarity Call
        </a>
      </div>

      <article className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        {loading && (
          <div className="flex items-center justify-center py-16 text-[#d4c4a8]/60" data-testid="post-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            <span className="font-body">Loading&hellip;</span>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16" data-testid="post-error">
            <p className="font-body text-[#d4c4a8]/60 mb-6">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 text-[#e89b47] hover:underline font-display tracking-wide text-sm"
            >
              Back to Blog <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {!loading && !error && post && (
          <>
            <p className="font-display text-xs text-[#e89b47] tracking-[0.3em] uppercase mb-4" data-testid="post-date">
              {formatDate(post.published_at)}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#e8dcc8] leading-tight mb-6" data-testid="post-title">
              {post.title}
            </h1>
            {post.author && (
              <p className="font-body text-sm text-[#d4c4a8]/60 mb-5" data-testid="post-author">
                By {post.author}
              </p>
            )}

            {/* Share row */}
            <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="post-share-row">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/api/share/blog/${slug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-xs font-display tracking-widest text-[#d4c4a8] border border-[#d4c4a8]/20 hover:border-[#e89b47] hover:text-[#e89b47] transition-colors"
                data-testid="post-share-linkedin-btn"
              >
                <Linkedin className="w-3.5 h-3.5" /> Share on LinkedIn
              </a>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/api/share/blog/${slug}`);
                  toast.success('Share link copied');
                }}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-xs font-display tracking-widest text-[#d4c4a8] border border-[#d4c4a8]/20 hover:border-[#e89b47] hover:text-[#e89b47] transition-colors"
                data-testid="post-share-copy-btn"
              >
                <Link2 className="w-3.5 h-3.5" /> Copy Link
              </button>
            </div>

            {post.cover_image && (
              <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10 border border-[#d4c4a8]/10">
                <CoverImage src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
              </div>
            )}

            {post.excerpt && (
              <p className="font-body text-lg text-[#d4c4a8]/70 italic mb-8 leading-relaxed" data-testid="post-excerpt">
                {post.excerpt}
              </p>
            )}

            {renderBody(post.body)}

            {post.linkedin_url && (
              <div className="mt-12 pt-8 border-t border-[#d4c4a8]/10">
                <a
                  href={post.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#e89b47] hover:underline font-display text-sm tracking-wide"
                  data-testid="post-linkedin-link"
                >
                  View original on LinkedIn <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {/* Related posts */}
            {related.length > 0 && (
              <div className="mt-14 pt-10 border-t border-[#d4c4a8]/10" data-testid="post-related-section">
                <h3 className="font-display text-xl text-[#e8dcc8] mb-6 text-center">
                  You Might Also <span className="text-[#e89b47]">Like</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {related.map((r) => (
                    <button
                      key={r.slug}
                      type="button"
                      onClick={() => navigate(`/blog/${r.slug}`)}
                      className="group text-left rounded-xl overflow-hidden bg-[#1e2a4a]/40 border border-[#d4c4a8]/10 hover:border-[#e89b47]/40 transition-all"
                      data-testid={`related-post-${r.slug}`}
                    >
                      <div className="aspect-[16/9] bg-[#0f1f35] overflow-hidden">
                        <CoverImage
                          src={r.cover_image}
                          alt={r.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <p className="font-body text-[10px] text-[#e89b47] tracking-wider uppercase mb-1.5">
                          {formatDate(r.published_at)}
                        </p>
                        <h4 className="font-display text-sm text-[#e8dcc8] leading-snug group-hover:text-[#e89b47] transition-colors line-clamp-2">
                          {r.title}
                        </h4>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-[#e89b47]/5 to-[#d4883a]/5 border border-[#e89b47]/20 text-center">
              <h3 className="font-display text-xl text-[#e8dcc8] mb-3">Ready to turn insight into strategy?</h3>
              <p className="font-body text-sm text-[#d4c4a8]/70 mb-6">Draw a Marketing Tarot reading or book a clarity call.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-2 h-11 px-6 text-sm bg-transparent border-2 border-[#d4c4a8]/30 text-[#d4c4a8] font-display tracking-widest rounded-xl transition-all hover:border-[#e89b47]/50 hover:text-[#e89b47]"
                  data-testid="post-cta-draw-btn"
                >
                  Draw a Card
                </button>
                <a
                  href="https://calendly.com/marketingtarot/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 h-11 px-6 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest rounded-xl shadow-lg shadow-[#e89b47]/25 transition-all hover:scale-105"
                  data-testid="post-cta-book-btn"
                >
                  Book a Clarity Call
                </a>
              </div>
            </div>
          </>
        )}
      </article>

      <SiteFooter />
    </div>
  );
};

export default BlogPostPage;
